import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileClock, Plus, Trash2, AlertTriangle } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import { PickerSelect } from "@/components/cam/AssetDrawer";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const STATUS = { active: { label: "Active", color: "#16a34a" }, expired: { label: "Expired", color: "#ef4444" }, cancelled: { label: "Cancelled", color: "#64748b" } };
const soonStr = () => new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10);
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Amc() {
  const { can, catName } = useCam();
  const canWrite = can.admin || can.store;
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dueOnly, setDueOnly] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([camApi.amc(dueOnly ? { due: 60 } : {}), camApi.vendors()]);
      setRows(a.amc || []); setVendors((v.vendors || []).filter((x) => x.is_active !== false));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueOnly]);

  const remove = async (id) => {
    if (!window.confirm("Delete this AMC contract?")) return;
    try { await camApi.removeAmc(id); toast.success("Deleted"); load(); } catch (e) { toast.error(e.message); }
  };

  const scopeLabel = (r) => r.asset ? `${r.asset.name} (${r.asset.asset_tag})` : r.category_id ? catName(r.category_id) : "Institute-wide";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><FileClock className="h-4 w-4 text-cobalt-500" /><div className="overline">Annual maintenance contracts</div></div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDueOnly((v) => !v)}
            className={`text-xs rounded-full px-3 py-1.5 border ${dueOnly ? "bg-amber-500 text-white border-amber-500" : "hover:bg-black/5"}`}>
            Due in 60 days
          </button>
          {canWrite && <Button size="sm" className="bg-cobalt-500 hover:bg-cobalt-600" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New AMC</Button>}
        </div>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-12 text-center text-sm text-muted-foreground">Loading contracts…</div>
      ) : rows.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center max-w-md mx-auto">
          <FileClock className="h-9 w-9 mx-auto text-muted-foreground/40" />
          <h3 className="font-display text-lg tracking-tight mt-3">{dueOnly ? "Nothing due in 60 days" : "No AMC contracts yet"}</h3>
          <p className="text-sm text-muted-foreground mt-1.5">Track annual maintenance contracts and get a heads-up before they lapse.</p>
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto touch-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-2.5">Scope</th><th className="px-4 py-2.5">Vendor</th><th className="px-4 py-2.5 hidden sm:table-cell">Coverage</th>
                <th className="px-4 py-2.5">Ends</th><th className="px-4 py-2.5 hidden md:table-cell">Cost</th><th className="px-4 py-2.5">Status</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const due = r.status === "active" && r.end_date && r.end_date >= todayStr() && r.end_date <= soonStr();
                  const expired = r.end_date && r.end_date < todayStr();
                  const sm = STATUS[r.status] || {};
                  return (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-4 py-2.5"><div className="font-medium truncate max-w-[220px]">{scopeLabel(r)}</div>{r.contract_no && <div className="text-[11px] text-mono text-muted-foreground">{r.contract_no}</div>}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.vendor?.name || "—"}</td>
                      <td className="px-4 py-2.5 hidden sm:table-cell text-muted-foreground truncate max-w-[200px]">{r.coverage || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className={due ? "text-amber-600 font-medium" : expired ? "text-red-500" : "text-muted-foreground"}>
                          {r.end_date ? new Date(r.end_date).toLocaleDateString() : "—"}
                        </span>
                        {due && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-600"><AlertTriangle className="h-3 w-3" />soon</span>}
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-muted-foreground">{r.cost != null ? inr(r.cost) : "—"}</td>
                      <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-xs font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: sm.color }} />{sm.label || r.status}</span></td>
                      <td className="px-4 py-2.5 text-right">{canWrite && <button onClick={() => remove(r.id)} className="p-1 text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && <AmcDialog vendors={vendors} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function AmcDialog({ vendors, onClose, onSaved }) {
  const { categories } = useCam();
  const [f, setF] = useState({ vendor_id: "", scope: "category", category_id: "", contract_no: "", coverage: "", start_date: "", end_date: "", cost: "", status: "active", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.end_date) { toast.error("An end date is needed to track renewals."); return; }
    setBusy(true);
    try {
      await camApi.createAmc({
        vendor_id: f.vendor_id ? Number(f.vendor_id) : null,
        category_id: f.scope === "category" && f.category_id ? Number(f.category_id) : null,
        contract_no: f.contract_no, coverage: f.coverage,
        start_date: f.start_date || null, end_date: f.end_date,
        cost: f.cost === "" ? null : Number(f.cost), status: f.status, notes: f.notes,
      });
      toast.success("AMC contract added"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto touch-scroll">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">New AMC contract</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Vendor</Label><PickerSelect placeholder="Select vendor" value={f.vendor_id} onChange={set("vendor_id")} options={vendors.map((v) => ({ value: String(v.id), label: v.name }))} className="mt-1" /></div>
          <div><Label className="text-xs">Applies to</Label><PickerSelect value={f.scope} onChange={(v) => setF({ ...f, scope: v, category_id: "" })} options={[{ value: "category", label: "A category" }, { value: "all", label: "Institute-wide" }]} className="mt-1" /></div>
          {f.scope === "category" && <div className="sm:col-span-2"><Label className="text-xs">Category</Label><PickerSelect placeholder="Select category" value={f.category_id} onChange={set("category_id")} options={categories.map((c) => ({ value: String(c.id), label: c.name }))} className="mt-1" /></div>}
          <div><Label className="text-xs">Contract no.</Label><Input className="mt-1" value={f.contract_no} onChange={set("contract_no")} /></div>
          <div><Label className="text-xs">Cost (₹)</Label><Input type="number" className="mt-1" value={f.cost} onChange={set("cost")} /></div>
          <div><Label className="text-xs">Start date</Label><Input type="date" className="mt-1" value={f.start_date} onChange={set("start_date")} /></div>
          <div><Label className="text-xs">End date *</Label><Input type="date" className="mt-1" value={f.end_date} onChange={set("end_date")} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Coverage</Label><Input className="mt-1" value={f.coverage} onChange={set("coverage")} placeholder="e.g. On-site, parts + labour" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Notes</Label><Textarea className="mt-1" rows={2} value={f.notes} onChange={set("notes")} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Saving…" : "Add contract"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
